import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { heroSlides } from '../mock';
import HeroTrustBadge from './HeroTrustBadge';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animating, setAnimating]       = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (fn) => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentSlide(fn);
      setAnimating(false);
    }, 300);
  };

  const nextSlide = () => goToSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => goToSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 100, behavior: 'smooth' });
    }
  };

  const slide = heroSlides[currentSlide];

  const trust = [
    '15+ anos de mercado',
    'Ribeirão Preto e região',
    'Diagnóstico gratuito',
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">

      {/* ── Background: imagem tech ─────────────────────────────────── */}
      <div className="absolute inset-0">
        <img
          src="./hero-tech-bg.png"
          alt="Fernando Tech — Suporte Técnico Premium em Ribeirão Preto"
          className="w-full h-full object-cover object-[78%_center] lg:object-center select-none"
          draggable={false}
        />
        {/* Overlay MOBILE: vertical — deixa o rosto visível no topo e escurece embaixo onde está o texto */}
        <div className="absolute inset-0 lg:hidden bg-gradient-to-b from-slate-950/35 via-slate-950/60 to-slate-950/95" />
        {/* Overlay DESKTOP: horizontal — escuro à esquerda (texto), aberto à direita (rosto) */}
        <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-900/10" />
        {/* Fade inferior → funde com a próxima seção */}
        <div className="absolute bottom-0 left-0 right-0 h-36 bg-gradient-to-t from-slate-950 to-transparent" />
        {/* Borda ciano sutil no topo */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      </div>

      {/* ── Conteúdo ────────────────────────────────────────────────── */}
      <div className="container mx-auto px-4 sm:px-8 relative z-10 pt-28 lg:pt-0">
        <div className="max-w-2xl xl:max-w-3xl">

          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
              Fernando Tech
            </span>
            <div className="h-px w-16 bg-gradient-to-r from-cyan-500 to-transparent" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Suporte Técnico Premium
            </span>
          </div>

          {/* H1 + subtítulo com transição */}
          <div
            className="mb-8"
            style={{
              opacity:    animating ? 0 : 1,
              transform:  animating ? 'translateY(12px)' : 'translateY(0)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight mb-5 tracking-tight">
              {slide.title}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed max-w-xl">
              {slide.subtitle}
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-3 mb-10">
            <Button
              onClick={(e) => scrollToSection(e, slide.link)}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-bold px-7 py-5 text-base shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105 rounded-full"
            >
              {slide.cta}
            </Button>
            <a
              href="https://wa.me/5516991378312?text=Olá!%20Vim%20pelo%20site%20da%20Fernando%20Tech%20e%20gostaria%20de%20mais%20informações."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-[13px] bg-white/8 hover:bg-white/14 border border-white/20 hover:border-emerald-400/50 text-white font-semibold text-base rounded-full transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            >
              <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
              </svg>
              Falar no WhatsApp
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap gap-4">
            {trust.map(t => (
              <div key={t} className="flex items-center gap-1.5 text-sm text-slate-400">
                <CheckCircle className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                {t}
              </div>
            ))}
          </div>

          {/* Trust Stats animados */}
          <HeroTrustBadge />
        </div>
      </div>

      {/* ── Setas de navegação ───────────────────────────────────────── */}
      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 bg-white/8 backdrop-blur-sm hover:bg-white/16 border border-white/10 text-white p-2.5 rounded-full transition-all hover:scale-110"
        aria-label="Slide anterior"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 bg-white/8 backdrop-blur-sm hover:bg-white/16 border border-white/10 text-white p-2.5 rounded-full transition-all hover:scale-110"
        aria-label="Próximo slide"
      >
        <ChevronRight size={22} />
      </button>

      {/* ── Indicadores ─────────────────────────────────────────────── */}
      <div className="absolute bottom-10 left-4 sm:left-8 z-20 flex gap-2 items-center">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(() => index)}
            className={`h-1.5 rounded-full transition-all duration-400 ${
              index === currentSlide
                ? 'w-8 bg-cyan-400'
                : 'w-2 bg-white/30 hover:bg-white/55'
            }`}
            aria-label={`Ir para slide ${index + 1}`}
          />
        ))}
        <span className="ml-3 text-[11px] text-slate-500 tabular-nums">
          {currentSlide + 1}/{heroSlides.length}
        </span>
      </div>
    </section>
  );
};

export default Hero;
